import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import ReactLoading from 'react-loading';
import { apiCall, formattedPrice, getBranches, getEmployes, getStatus, getTypes } from '../../helpers/MainHelper';
import './ReportsCenter.css';
import ReportFilterLookup from '../../components/ReportFilterLookup';
import ReportOperationsTable from '../../components/ReportOperationsTable';

const toInputDate = (date) => {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const currentMonth = () => {
  const now = new Date();
  return {
    start_date: toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    end_date: toInputDate(now),
  };
};

const formatNumber = (value, maximumFractionDigits = 0) => new Intl.NumberFormat('tr-TR', {
  maximumFractionDigits,
}).format(Number(value) || 0);

const formatDate = (value) => value
  ? new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value))
  : '-';

const changeLabel = (value) => {
  if (value === null || value === undefined) return 'Önceki dönem verisi yok';
  if (value === 0) return 'Önceki dönemle aynı';
  return `${value > 0 ? '+' : ''}%${formatNumber(value, 1)} önceki döneme göre`;
};

const MetricCard = ({ tone = 'blue', icon, label, value, detail, change, warning }) => (
  <div className={`rz-metric rz-tone-${tone}`}>
    <div className="rz-metric-top">
      <span className="rz-metric-icon"><i className={`fa ${icon}`} /></span>
      {change !== undefined && (
        <span className={`rz-change ${change === null ? 'neutral' : change >= 0 ? 'positive' : 'negative'}`}>
          {change === null ? 'Yeni' : `${change > 0 ? '+' : ''}${formatNumber(change, 1)}%`}
        </span>
      )}
    </div>
    <span className="rz-metric-label">{label}</span>
    <strong className="rz-metric-value">{value}</strong>
    <span className={`rz-metric-detail ${warning ? 'warning' : ''}`}>{detail}</span>
  </div>
);

const EmptyState = ({ children = 'Bu filtrelerde veri bulunamadı.' }) => (
  <div className="rz-empty"><i className="fa fa-inbox" /><span>{children}</span></div>
);

const SectionTitle = ({ eyebrow, title, description, action }) => (
  <div className="rz-section-title">
    <div>
      <span>{eyebrow}</span>
      <h3>{title}</h3>
      {description && <p>{description}</p>}
    </div>
    {action}
  </div>
);

const Distribution = ({ items = [], valueKey = 'count', money = false }) => {
  const maximum = Math.max(...items.map(item => Math.abs(Number(item[valueKey]) || 0)), 1);
  if (!items.length) return <EmptyState />;

  return (
    <div className="rz-distribution">
      {items.map((item, index) => (
        <div className="rz-distribution-row" key={`${item.name}-${index}`}>
          <div className="rz-distribution-label">
            <span>{item.name}</span>
            <strong>{money ? formattedPrice(item[valueKey]) : formatNumber(item[valueKey])}</strong>
          </div>
          <div className="rz-track"><span className={Number(item[valueKey]) < 0 ? 'negative' : ''} style={{ width: `${Math.max(4, Math.abs(Number(item[valueKey]) || 0) / maximum * 100)}%` }} /></div>
        </div>
      ))}
    </div>
  );
};

const AgingChart = ({ data = {} }) => {
  const rows = [
    ['Vadesi gelmemiş', data.not_due, 'safe'],
    ['0–7 gün gecikmiş', data.days_0_7, 'watch'],
    ['8–30 gün gecikmiş', data.days_8_30, 'warning'],
    ['31–60 gün gecikmiş', data.days_31_60, 'danger'],
    ['60+ gün gecikmiş', data.days_60_plus, 'critical'],
  ];
  const maximum = Math.max(...rows.map(row => Number(row[1]) || 0), 1);

  return (
    <div className="rz-aging">
      {rows.map(([label, value, tone]) => (
        <div className="rz-aging-row" key={label}>
          <div><span>{label}</span><strong>{formattedPrice(value || 0)}</strong></div>
          <div className="rz-aging-track"><span className={tone} style={{ width: `${(Number(value) || 0) / maximum * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
};

const ReportsCenter = () => {
  const initialPeriod = useMemo(currentMonth, []);
  const [filters, setFilters] = useState({
    ...initialPeriod,
    branch_id: '',
    operation_type_id: '',
    status_id: '',
    employee_id: '',
    customer_id: '',
    vehicle_id: '',
  });
  const [branches, setBranches] = useState([]);
  const [operationTypes, setOperationTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [report, setReport] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preset, setPresetKey] = useState('month');
  const [extraFiltersOpen, setExtraFiltersOpen] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');
  const [vehicleQuery, setVehicleQuery] = useState('');
  const [customerSelection, setCustomerSelection] = useState(null);
  const [vehicleSelection, setVehicleSelection] = useState(null);
  const [filterError, setFilterError] = useState('');
  const requestSequence = useRef(0);

  const loadReport = useCallback(async (nextFilters) => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    setError('');
    try {
      const payload = new FormData();
      Object.keys(nextFilters).forEach(key => {
        if (nextFilters[key] !== '' && nextFilters[key] !== null) payload.append(key, nextFilters[key]);
      });
      const response = await apiCall('business/reports-v2/overview', 'post', payload);
      if (requestId === requestSequence.current) {
        setReport(response);
        setAppliedFilters({ ...nextFilters });
      }
    } catch (requestError) {
      if (requestId === requestSequence.current) {
        setError('Rapor verileri alınamadı. Lütfen filtreleri kontrol edip tekrar deneyin.');
      }
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    Promise.allSettled([getBranches(), getTypes(), getStatus(), getEmployes()]).then(results => {
      if (!mounted) return;
      if (results[0].status === 'fulfilled') setBranches(results[0].value || []);
      if (results[1].status === 'fulfilled') setOperationTypes(results[1].value || []);
      if (results[2].status === 'fulfilled') setStatuses(results[2].value || []);
      if (results[3].status === 'fulfilled') setEmployees(results[3].value || []);
    });
    loadReport({
      ...initialPeriod,
      branch_id: '',
      operation_type_id: '',
      status_id: '',
      employee_id: '',
      customer_id: '',
      vehicle_id: '',
    });
    return () => { mounted = false; };
  }, [initialPeriod, loadReport]);

  const setPreset = (preset) => {
    setPresetKey(preset);
    if (preset === 'custom') return;
    const now = new Date();
    let period;
    if (preset === 'today') {
      period = { start_date: toInputDate(now), end_date: toInputDate(now) };
    } else if (preset === 'yesterday') {
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      period = { start_date: toInputDate(yesterday), end_date: toInputDate(yesterday) };
    } else if (preset === 'week') {
      const start = new Date(now);
      start.setDate(now.getDate() - 6);
      period = { start_date: toInputDate(start), end_date: toInputDate(now) };
    } else if (preset === 'last-30') {
      const start = new Date(now);
      start.setDate(now.getDate() - 29);
      period = { start_date: toInputDate(start), end_date: toInputDate(now) };
    } else if (preset === 'this-week') {
      const start = new Date(now);
      start.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      period = { start_date: toInputDate(start), end_date: toInputDate(now) };
    } else if (preset === 'last-week') {
      const start = new Date(now);
      start.setDate(now.getDate() - ((now.getDay() + 6) % 7) - 7);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      period = { start_date: toInputDate(start), end_date: toInputDate(end) };
    } else if (preset === 'last-month') {
      period = {
        start_date: toInputDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        end_date: toInputDate(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    } else if (preset === 'year') {
      period = {
        start_date: toInputDate(new Date(now.getFullYear(), 0, 1)),
        end_date: toInputDate(now),
      };
    } else if (preset === 'all-time') {
      period = { start_date: '1970-01-01', end_date: toInputDate(now) };
    } else {
      period = currentMonth();
    }
    const nextFilters = { ...filters, ...period };
    setFilters(nextFilters);
  };

  const submitFilters = (event) => {
    event.preventDefault();
    if ((customerQuery.trim() && !customerSelection) || (vehicleQuery.trim() && !vehicleSelection)) {
      setFilterError('Müşteri ve plaka için listeden bir kayıt seçin veya arama alanını temizleyin.');
      return;
    }
    setFilterError('');
    loadReport(filters);
  };

  const resetFilters = () => {
    const nextFilters = {
      ...currentMonth(),
      branch_id: '',
      operation_type_id: '',
      status_id: '',
      employee_id: '',
      customer_id: '',
      vehicle_id: '',
    };
    setCustomerQuery(''); setVehicleQuery('');
    setCustomerSelection(null); setVehicleSelection(null);
    setPresetKey('month'); setExtraFiltersOpen(false); setFilterError('');
    setFilters(nextFilters);
    loadReport(nextFilters);
  };

  const exportSummary = () => {
    if (!report) return;
    const rows = [
      ['Dönem', `${report.meta.start_date} / ${report.meta.end_date}`],
      ['Açılan servis', report.overview.opened_services],
      ['Tamamlanan servis', report.overview.completed_services],
      ['Aktif servis', report.overview.active_services],
      ['Net servis cirosu', report.overview.net_revenue],
      ['Brüt kâr', report.overview.gross_profit],
      ['Net nakit hareketi', report.overview.cash_flow],
      ['Kalan müşteri alacağı', report.overview.receivables_balance],
      ['Kalan tedarikçi borcu', report.overview.supplier_debt_balance],
      ['Kritik stok', report.overview.low_stock_count],
    ];
    const csv = ['Gösterge;Değer', ...rows.map(row => row.join(';'))].join('\n');
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `rapor-merkezi-${report.meta.start_date}-${report.meta.end_date}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const insights = report ? [
    report.overview.aged_services > 0 && `${report.overview.aged_services} aktif servis 7 günden uzun süredir açık.`,
    report.financials.missing_cost_count > 0 && `${report.financials.missing_cost_count} tamamlanan serviste maliyet bilgisi eksik.`,
    report.balances.receivables.overdue > 0 && `${formattedPrice(report.balances.receivables.overdue)} gecikmiş müşteri alacağı bulunuyor.`,
    report.stock.low_stock_count > 0 && `${report.stock.low_stock_count} ürün minimum stok seviyesinde veya altında.`,
  ].filter(Boolean) : [];

  const serviceFilterCount = ['operation_type_id', 'status_id', 'employee_id', 'customer_id', 'vehicle_id']
    .filter(key => filters[key] !== '').length;
  const extraFilterCount = ['operation_type_id', 'status_id', 'employee_id'].filter(key => filters[key] !== '').length;

  const tabs = [
    ['overview', 'Genel Bakış', 'fa-th-large'],
    ['operations', 'Operasyon', 'fa-wrench'],
    ['finance', 'Finans & Nakit', 'fa-line-chart'],
    ['balances', 'Cari', 'fa-balance-scale'],
    ['customers', 'Müşteri', 'fa-users'],
    ['stock', 'Stok', 'fa-cubes'],
  ];

  return (
    <div className="rz-page">
      <div className="container-fluid">
        <PageHeader HeaderText="Rapor Merkezi" Breadcrumb={[{ name: 'Rapor Merkezi', navigate: '' }]} />

        <section className="rz-hero">
          <div className="rz-hero-copy">
            <span className="rz-eyebrow"><i className="fa fa-diamond" /> YÖNETİCİ RAPORLARI</span>
            <h1>Servisin nabzı, tek ekranda.</h1>
            <p>Operasyon, kârlılık, nakit, cari, müşteri ve stok verilerini aynı dönem ve şube filtresiyle yönetin.</p>
          </div>
          <div className="rz-hero-actions">
            <button type="button" onClick={exportSummary} disabled={!report}><i className="fa fa-download" /> CSV İndir</button>
            <button type="button" onClick={() => window.print()} disabled={!report}><i className="fa fa-print" /> Yazdır / PDF</button>
          </div>
        </section>

        <form className="rz-filter" onSubmit={submitFilters}>
          <div className="rz-filter-main">
            <label><span>Dönem</span><select aria-label="Dönem" value={preset} onChange={event => setPreset(event.target.value)}>
              <option value="custom">Özel aralık</option><option value="today">Bugün</option><option value="yesterday">Dün</option>
              <option value="week">Son 7 gün</option><option value="last-30">Son 30 gün</option>
              <option value="this-week">Bu hafta</option><option value="last-week">Geçen hafta</option>
              <option value="month">Bu ay</option><option value="last-month">Geçen ay</option>
              <option value="year">Bu yıl</option><option value="all-time">Tüm zamanlar</option>
            </select></label>
            <label><span>Başlangıç</span><input type="date" required max={filters.end_date} value={filters.start_date} onChange={e => { setPresetKey('custom'); setFilters({ ...filters, start_date: e.target.value }); }} /></label>
            <label><span>Bitiş</span><input type="date" required min={filters.start_date} value={filters.end_date} onChange={e => { setPresetKey('custom'); setFilters({ ...filters, end_date: e.target.value }); }} /></label>
            <label><span>Şube</span><select value={filters.branch_id} onChange={e => setFilters({ ...filters, branch_id: e.target.value })}>
              <option value="">Tüm şubeler</option>
              {branches.map(branch => <option value={branch.id} key={branch.id}>{branch.name}</option>)}
            </select></label>
            <div className="rz-filter-actions">
              <button className="rz-extra-toggle" type="button" aria-expanded={extraFiltersOpen} aria-controls="rz-extra-filters" onClick={() => setExtraFiltersOpen(!extraFiltersOpen)}>
                <i className="fa fa-sliders" /> Diğer filtreler {extraFilterCount > 0 && <b>{extraFilterCount}</b>}
              </button>
              <button className="rz-apply" type="submit" disabled={loading}><i className="fa fa-filter" /> {loading ? 'Hazırlanıyor' : 'Uygula'}</button>
              <button className="rz-reset" type="button" onClick={resetFilters} title="Filtreleri temizle" aria-label="Filtreleri temizle"><i className="fa fa-refresh" /></button>
            </div>
          </div>
          <div className="rz-filter-searches">
            <ReportFilterLookup id="rz-customer" label="Müşteri" type="customer" query={customerQuery} selected={customerSelection}
              onQueryChange={text => { setCustomerQuery(text); setCustomerSelection(null); setFilterError(''); setFilters(previous => ({ ...previous, customer_id: '' })); }}
              onSelect={option => {
                const clearVehicle = vehicleSelection && String(vehicleSelection.customer_id) !== String(option.value);
                setCustomerQuery(option.label); setCustomerSelection(option); setFilterError('');
                if (clearVehicle) { setVehicleSelection(null); setVehicleQuery(''); }
                setFilters(previous => ({ ...previous, customer_id: option.value, ...(clearVehicle ? { vehicle_id: '' } : {}) }));
              }} />
            <ReportFilterLookup id="rz-vehicle" label="Plaka" type="vehicle" query={vehicleQuery} selected={vehicleSelection} customerId={filters.customer_id}
              onQueryChange={text => { setVehicleQuery(text); setVehicleSelection(null); setFilterError(''); setFilters(previous => ({ ...previous, vehicle_id: '' })); }}
              onSelect={option => { setVehicleQuery(option.label); setVehicleSelection(option); setFilterError(''); setFilters(previous => ({ ...previous, vehicle_id: option.value })); }} />
          </div>
          {extraFiltersOpen && <div className="rz-advanced-filters" id="rz-extra-filters">
            <label><span>Servis Tipi</span><select value={filters.operation_type_id} onChange={e => setFilters({ ...filters, operation_type_id: e.target.value })}>
              <option value="">Tüm servis tipleri</option>
              {operationTypes.map(type => <option value={type.id} key={type.id}>{type.name}</option>)}
            </select></label>
            <label><span>Servis Durumu</span><select value={filters.status_id} onChange={e => setFilters({ ...filters, status_id: e.target.value })}>
              <option value="">Tüm durumlar</option>
              {statuses.map(status => <option value={status.id} key={status.id}>{status.name}</option>)}
            </select></label>
            <label><span>Sorumlu Personel</span><select value={filters.employee_id} onChange={e => setFilters({ ...filters, employee_id: e.target.value })}>
              <option value="">Tüm personel</option>
              <option value="unassigned">Atanmamış servisler</option>
              {employees.map(employee => <option value={employee.id} key={employee.id}>{`${employee.name || ''} ${employee.surname || ''}`.trim()}</option>)}
            </select></label>
          </div>}
          {filterError && <div className="rz-filter-error" role="alert">{filterError}</div>}
            <div className={`rz-filter-scope ${serviceFilterCount ? 'active' : ''}`}>
              <i className="fa fa-info-circle" />
              <span>{serviceFilterCount ? `${serviceFilterCount} servis filtresi seçildi.` : 'Servis filtreleri isteğe bağlıdır.'} Bunlar nakit, cari ve stok tutarlarını değiştirmez.</span>
            </div>
        </form>

        <div className="rz-tabs" role="tablist">
          {tabs.map(([key, label, icon]) => (
            <button type="button" role="tab" aria-selected={activeTab === key} className={activeTab === key ? 'active' : ''} key={key} onClick={() => setActiveTab(key)}>
              <i className={`fa ${icon}`} /><span>{label}</span>
            </button>
          ))}
        </div>

        {loading && <div className="rz-loading"><ReactLoading type="spin" color="#2f6fed" height={42} width={42} /><span>Rapor hazırlanıyor…</span></div>}
        {!loading && error && <div className="rz-error"><i className="fa fa-exclamation-triangle" />{error}</div>}

        {!loading && report && (
          <div className="rz-content">
            {activeTab === 'overview' && (
              <>
                <div className="rz-period-note"><i className="fa fa-calendar" /><span><strong>{formatDate(report.meta.start_date)}</strong> – <strong>{formatDate(report.meta.end_date)}</strong></span><em>Son güncelleme {new Date(report.meta.generated_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</em></div>
                {insights.length > 0 && <div className="rz-insights"><span><i className="fa fa-bolt" /> Dikkat isteyenler</span><div>{insights.map(item => <p key={item}>{item}</p>)}</div></div>}
                <div className="rz-metric-grid rz-grid-4">
                  <MetricCard tone="blue" icon="fa-check-circle" label="Tamamlanan Servis" value={formatNumber(report.overview.completed_services)} detail={`${formatNumber(report.overview.opened_services)} yeni servis açıldı`} />
                  <MetricCard tone="indigo" icon="fa-try" label="Net Servis Cirosu" value={formattedPrice(report.overview.net_revenue)} detail={changeLabel(report.overview.revenue_change)} change={report.overview.revenue_change} />
                  <MetricCard tone="green" icon="fa-trophy" label="Brüt Kâr" value={formattedPrice(report.overview.gross_profit)} detail={`Brüt marj %${formatNumber(report.overview.gross_margin, 1)}`} change={report.overview.profit_change} />
                  <MetricCard tone={report.overview.cash_flow >= 0 ? 'cyan' : 'red'} icon="fa-exchange" label="Net Nakit Hareketi" value={formattedPrice(report.overview.cash_flow)} detail="Tahsilat + manuel gelir − çıkışlar" />
                  <MetricCard tone="orange" icon="fa-car" label="Aktif Servis" value={formatNumber(report.overview.active_services)} detail={`${formatNumber(report.overview.aged_services)} kayıt 7 günü geçti`} warning={report.overview.aged_services > 0} />
                  <MetricCard tone="purple" icon="fa-clock-o" label="Ortalama Servis Süresi" value={`${formatNumber(report.operations.average_cycle_hours, 1)} saat`} detail="Tamamlanan servisler" />
                  <MetricCard tone="rose" icon="fa-arrow-circle-o-down" label="Kalan Alacak" value={formattedPrice(report.overview.receivables_balance)} detail={`${formattedPrice(report.balances.receivables.overdue)} gecikmiş`} warning={report.balances.receivables.overdue > 0} />
                  <MetricCard tone="amber" icon="fa-cubes" label="Kritik Stok" value={formatNumber(report.overview.low_stock_count)} detail={`${formatNumber(report.stock.out_of_stock_count)} ürün stokta yok`} warning={report.overview.low_stock_count > 0} />
                </div>
                <div className="rz-two-column">
                  <section className="rz-panel">
                    <SectionTitle eyebrow="CANLI İŞ YÜKÜ" title="Aktif servislerin durum dağılımı" description="Tamamlanmamış tüm servislerin bugünkü görünümü" />
                    <Distribution items={report.operations.status_distribution} />
                  </section>
                  <section className="rz-panel">
                    <SectionTitle eyebrow="DÖNEM PERFORMANSI" title="Servis tipine göre ciro" description="Tamamlanma tarihi seçilen dönemde olan servisler" />
                    <Distribution items={report.operations.type_performance} valueKey="revenue" money />
                  </section>
                </div>
              </>
            )}

            {activeTab === 'operations' && (
              <>
                <div className="rz-metric-grid rz-grid-4">
                  <MetricCard tone="blue" icon="fa-plus-circle" label="Dönemde Açılan" value={formatNumber(report.operations.opened_count)} detail="Yeni iş emirleri" />
                  <MetricCard tone="green" icon="fa-check" label="Dönemde Tamamlanan" value={formatNumber(report.operations.completed_count)} detail="Tamamlanma tarihine göre" />
                  <MetricCard tone="orange" icon="fa-hourglass-half" label="Aktif İş Yükü" value={formatNumber(report.operations.active_count)} detail={`${report.operations.aged_count} servis 7 günü geçti`} warning={report.operations.aged_count > 0} />
                  <MetricCard tone="rose" icon="fa-user-times" label="Atanmamış Servis" value={formatNumber(report.operations.unassigned_count)} detail="Sorumlu çalışan bekliyor" warning={report.operations.unassigned_count > 0} />
                </div>
                {appliedFilters && <ReportOperationsTable key={JSON.stringify(appliedFilters)} filters={appliedFilters} />}
                <div className="rz-two-column">
                  <section className="rz-panel"><SectionTitle eyebrow="DAĞILIM" title="Aktif servis durumları" /><Distribution items={report.operations.status_distribution} /></section>
                  <section className="rz-panel"><SectionTitle eyebrow="VERİMLİLİK" title="Tamamlanan servislerin tip dağılımı" description="Tamamlanma tarihi seçilen dönemde olan servisler" /><Distribution items={report.operations.type_performance} valueKey="count" /></section>
                </div>
              </>
            )}

            {activeTab === 'finance' && (
              <>
                <div className="rz-definition"><i className="fa fa-info-circle" /><span><strong>Brüt kâr</strong>, servis cirosundan yalnızca servis kalemlerindeki doğrudan maliyetlerin çıkarılmasıdır. Kira, maaş ve genel giderleri içermez.</span></div>
                <div className="rz-metric-grid rz-grid-4">
                  <MetricCard tone="indigo" icon="fa-line-chart" label="Net Servis Cirosu" value={formattedPrice(report.financials.net_revenue)} detail={changeLabel(report.financials.revenue_change)} change={report.financials.revenue_change} />
                  <MetricCard tone="red" icon="fa-shopping-cart" label="Doğrudan Maliyet" value={formattedPrice(report.financials.direct_cost)} detail={`${report.financials.missing_cost_count} serviste maliyet eksik`} warning={report.financials.missing_cost_count > 0} />
                  <MetricCard tone="green" icon="fa-trophy" label="Brüt Kâr" value={formattedPrice(report.financials.gross_profit)} detail={`Brüt marj %${formatNumber(report.financials.gross_margin, 1)}`} change={report.financials.profit_change} />
                  <MetricCard tone="blue" icon="fa-ticket" label="Ortalama Servis Tutarı" value={formattedPrice(report.financials.average_ticket)} detail={`${report.financials.service_count} tamamlanan servis`} />
                  <MetricCard tone="purple" icon="fa-percent" label="KDV" value={formattedPrice(report.financials.vat)} detail="Hesaplanan KDV" />
                  <MetricCard tone="orange" icon="fa-tag" label="İndirim" value={formattedPrice(report.financials.discount)} detail="Toplam uygulanan indirim" />
                  <MetricCard tone="cyan" icon="fa-arrow-down" label="Nakit Girişi" value={formattedPrice(report.financials.cash.cash_in)} detail="Tahsilat + manuel gelir" />
                  <MetricCard tone="rose" icon="fa-arrow-up" label="Nakit Çıkışı" value={formattedPrice(report.financials.cash.cash_out)} detail="Tedarikçi ödemesi + gider" />
                </div>
                <div className="rz-two-column">
                  <section className="rz-panel"><SectionTitle eyebrow="NAKİT AKIŞI" title="Dönem hareketleri" /><div className="rz-money-list">
                    <div><span>Müşteri tahsilatları</span><strong className="positive">+{formattedPrice(report.financials.cash.customer_collections)}</strong></div>
                    <div><span>Manuel gelirler</span><strong className="positive">+{formattedPrice(report.financials.cash.manual_income)}</strong></div>
                    <div><span>Tedarikçi ödemeleri</span><strong className="negative">−{formattedPrice(report.financials.cash.supplier_payments)}</strong></div>
                    <div><span>İşçilik hakediş ödemeleri</span><strong className="negative">−{formattedPrice(report.financials.cash.labor_payments)}</strong></div>
                    <div><span>Manuel giderler</span><strong className="negative">−{formattedPrice(report.financials.cash.manual_expense)}</strong></div>
                    <div className="total"><span>Net nakit hareketi</span><strong>{formattedPrice(report.financials.cash.net_cash_flow)}</strong></div>
                  </div></section>
                  <section className="rz-panel"><SectionTitle eyebrow="KÂRLILIK" title="Servis tipleri" /><Distribution items={report.financials.by_service_type} valueKey="gross_profit" money /></section>
                </div>
              </>
            )}

            {activeTab === 'balances' && (
              <>
                <div className="rz-definition"><i className="fa fa-calendar-check-o" /><span>Bu bölüm dönem toplamını değil, <strong>{formatDate(report.balances.as_of)} gün sonu itibarıyla</strong> açık bakiyeyi gösterir.</span></div>
                <div className="rz-metric-grid rz-grid-4">
                  <MetricCard tone="blue" icon="fa-arrow-circle-o-down" label="Müşteri Alacağı" value={formattedPrice(report.balances.receivables.balance)} detail={`${formattedPrice(report.balances.receivables.overdue)} gecikmiş`} warning={report.balances.receivables.overdue > 0} />
                  <MetricCard tone="red" icon="fa-arrow-circle-o-up" label="Tedarikçi Borcu" value={formattedPrice(report.balances.supplier_debts.balance)} detail={`${formattedPrice(report.balances.supplier_debts.overdue)} gecikmiş`} warning={report.balances.supplier_debts.overdue > 0} />
                  <MetricCard tone={report.balances.net_position >= 0 ? 'green' : 'orange'} icon="fa-balance-scale" label="Net Cari Pozisyon" value={formattedPrice(report.balances.net_position)} detail="Alacak − tedarikçi borcu" />
                  <MetricCard tone="cyan" icon="fa-credit-card" label="Kredi Bakiyeleri" value={formattedPrice(report.balances.receivables.customer_credits + report.balances.supplier_debts.supplier_credits)} detail="Fazla tahsilat ve ödemeler" />
                  <MetricCard tone="orange" icon="fa-handshake-o" label="Kalan Usta Hakedişi" value={formattedPrice(report.balances.labor_balance)} detail="Kazanılmış hakediş − ödemeler; net cari pozisyona dahil değil" />
                </div>
                <div className="rz-two-column">
                  <section className="rz-panel"><SectionTitle eyebrow="ALACAK YAŞLANDIRMA" title="Müşteri alacakları" /><AgingChart data={report.balances.receivables.aging} /></section>
                  <section className="rz-panel"><SectionTitle eyebrow="BORÇ YAŞLANDIRMA" title="Tedarikçi borçları" /><AgingChart data={report.balances.supplier_debts.aging} /></section>
                </div>
                <div className="rz-two-column">
                  <section className="rz-panel"><SectionTitle eyebrow="ÖNCELİKLİ TAHSİLAT" title="En yüksek müşteri bakiyeleri" action={<Link className="rz-link-button" to="/receivables">Cari listesi <i className="fa fa-arrow-right" /></Link>} />
                    <div className="rz-account-list">{report.balances.receivables.top_accounts.length ? report.balances.receivables.top_accounts.map((account, index) => <Link to={`/customer-detail/${account.id}`} key={account.id}><span><b>{index + 1}</b>{account.name}</span><strong>{formattedPrice(account.balance)}</strong></Link>) : <EmptyState />}</div>
                  </section>
                  <section className="rz-panel"><SectionTitle eyebrow="ÖNCELİKLİ ÖDEME" title="En yüksek tedarikçi bakiyeleri" action={<Link className="rz-link-button" to="/receivables-suppliers">Borç listesi <i className="fa fa-arrow-right" /></Link>} />
                    <div className="rz-account-list">{report.balances.supplier_debts.top_accounts.length ? report.balances.supplier_debts.top_accounts.map((account, index) => <Link to={`/supplier-detail/${account.id}`} key={account.id}><span><b>{index + 1}</b>{account.name}</span><strong>{formattedPrice(account.balance)}</strong></Link>) : <EmptyState />}</div>
                  </section>
                </div>
              </>
            )}

            {activeTab === 'customers' && (
              <>
                <div className="rz-metric-grid rz-grid-4">
                  <MetricCard tone="blue" icon="fa-users" label="Toplam Müşteri" value={formatNumber(report.customers.total_customers)} detail="Tedarikçiler hariç" />
                  <MetricCard tone="cyan" icon="fa-user-plus" label="Yeni Müşteri" value={formatNumber(report.customers.new_customers)} detail="Seçilen dönemde kaydedilen" />
                  <MetricCard tone="purple" icon="fa-refresh" label="Tekrar Gelen" value={formatNumber(report.customers.returning_customers)} detail={`Tekrar gelme oranı %${formatNumber(report.customers.repeat_rate, 1)}`} />
                  <MetricCard tone="amber" icon="fa-star" label="Ortalama Değerlendirme" value={report.customers.average_rating === null ? 'Veri yok' : `${formatNumber(report.customers.average_rating, 1)} / 5`} detail={`${report.customers.review_count} değerlendirme`} />
                </div>
                <section className="rz-panel"><SectionTitle eyebrow="MÜŞTERİ DEĞERİ" title="Dönemin öne çıkan müşterileri" description="Tamamlanan servis cirosuna göre sıralanır" action={<Link className="rz-link-button" to="/customers-all">Tüm müşteriler <i className="fa fa-arrow-right" /></Link>} />
                  {report.customers.top_customers.length ? <div className="table-responsive"><table className="rz-table"><thead><tr><th>Müşteri</th><th>Servis Sayısı</th><th>Net Ciro</th><th>Son Servis</th><th /></tr></thead><tbody>{report.customers.top_customers.map(customer => <tr key={customer.id}><td><strong>{customer.name}</strong></td><td>{customer.service_count}</td><td>{formattedPrice(customer.revenue)}</td><td>{formatDate(customer.last_service_at)}</td><td><Link className="rz-row-action" to={`/customer-detail/${customer.id}`}><i className="fa fa-arrow-right" /></Link></td></tr>)}</tbody></table></div> : <EmptyState />}
                </section>
              </>
            )}

            {activeTab === 'stock' && (
              <>
                <div className="rz-definition warning"><i className="fa fa-exclamation-circle" /><span>Stok değeri ürün kartındaki <strong>güncel birim fiyatla tahmini</strong> hesaplanır. Tarihsel alış maliyeti kaydı olmadığı için muhasebe stok maliyeti olarak kullanılmamalıdır.</span></div>
                <div className="rz-metric-grid rz-grid-4">
                  <MetricCard tone="blue" icon="fa-cube" label="Aktif Ürün" value={formatNumber(report.stock.total_items)} detail={`${report.stock.stocked_items} ürün stokta`} />
                  <MetricCard tone="indigo" icon="fa-try" label="Tahmini Stok Değeri" value={formattedPrice(report.stock.estimated_retail_value)} detail="Güncel ürün fiyatıyla" />
                  <MetricCard tone="orange" icon="fa-exclamation-triangle" label="Düşük Stok" value={formatNumber(report.stock.low_stock_count)} detail="Minimum seviyede veya altında" warning={report.stock.low_stock_count > 0} />
                  <MetricCard tone="red" icon="fa-ban" label="Stokta Yok" value={formatNumber(report.stock.out_of_stock_count)} detail={`${report.stock.movement_count} dönem hareketi`} warning={report.stock.out_of_stock_count > 0} />
                </div>
                <section className="rz-panel"><SectionTitle eyebrow="SATIN ALMA ÖNCELİĞİ" title="Kritik stok listesi" description="Eksik miktarı en yüksek ürünler" action={<Link className="rz-link-button" to="/stock/items">Stok kartları <i className="fa fa-arrow-right" /></Link>} />
                  {report.stock.low_stock_items.length ? <div className="table-responsive"><table className="rz-table"><thead><tr><th>Ürün</th><th>Depo</th><th>Mevcut</th><th>Minimum</th><th>Eksik</th></tr></thead><tbody>{report.stock.low_stock_items.map((item, index) => <tr key={`${item.sku}-${index}`}><td><strong>{item.item}</strong><small>{item.sku || '-'}</small></td><td>{item.warehouse}</td><td>{formatNumber(item.available, 2)} {item.unit}</td><td>{formatNumber(item.minimum, 2)} {item.unit}</td><td><span className="rz-shortage">{formatNumber(item.shortage, 2)} {item.unit}</span></td></tr>)}</tbody></table></div> : <EmptyState>Kritik stok bulunmuyor.</EmptyState>}
                </section>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsCenter;
